'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';

interface NavSection {
  label: string;
  anchor: string;
  tooltip?: string;
}

interface StickyAnchorNavProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const StickyAnchorNav = ({ isOpen = false, onToggle }: StickyAnchorNavProps) => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const sections: NavSection[] = [
    { label: t('nav.benefits'), anchor: 'beneficios', tooltip: t('nav.tooltip.benefits') },
    { label: t('nav.howItWorks'), anchor: 'como-funciona', tooltip: t('nav.tooltip.howItWorks') },
    { label: t('nav.commission'), anchor: 'comisiones', tooltip: t('nav.tooltip.commission') },
    { label: t('nav.testimonials'), anchor: 'testimonios', tooltip: t('nav.tooltip.testimonials') },
    { label: t('nav.community'), anchor: 'comunidad', tooltip: t('nav.tooltip.community') },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
      const sectionEls = sections.map(s => document.getElementById(s.anchor));
      const current = sectionEls.find(el => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top <= 120 && rect.bottom >= 120;
      });
      if (current) setActiveSection(current.id);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sections]);

  const scrollToSection = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) {
      const offset = 80;
      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
    if (isMobile && onToggle) onToggle();
  };

  const scrollToApply = () => {
    scrollToSection('aplicar');
    if (isMobile && onToggle) onToggle();
  };

  if (isMobile) {
    return (
      <>
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-350 ${
          isScrolled ? 'bg-white/95 backdrop-blur-xl border-b border-[#E8EDF5]' : 'bg-transparent'
        }`}>
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#0A1628] flex items-center justify-center">
                <Icon name="SparklesIcon" size={16} className="text-white" />
              </div>
              <span className="font-display font-bold text-[#0A1628] text-base">Neuroakashic™</span>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="mobile" />
              <button
                onClick={onToggle}
                className="p-2 rounded-xl bg-[#F4F7FC] hover:bg-[#EEF4FD] transition-colors"
                aria-label="Toggle menu"
              >
                <Icon name={isOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} className="text-[#0A1628]" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        <div className={`fixed inset-0 z-40 transition-all duration-350 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onToggle} />
          <nav className={`absolute right-0 top-0 h-full w-72 bg-white shadow-2xl transform transition-transform duration-350 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
            <div className="p-6 pt-20 space-y-1">
              {sections.map((section) => (
                <button
                  key={section.anchor}
                  onClick={() => scrollToSection(section.anchor)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-250 ${
                    activeSection === section.anchor
                      ? 'bg-[#EEF4FD] text-[#1D6FE8]'
                      : 'text-[#0A1628] hover:bg-[#F4F7FC]'
                  }`}
                >
                  <span className="font-body font-medium text-sm">{section.label}</span>
                </button>
              ))}
              <div className="pt-4">
                <button
                  onClick={scrollToApply}
                  className="w-full bg-[#0A1628] text-white font-display font-semibold text-sm py-3.5 rounded-xl hover:bg-[#1D6FE8] transition-colors"
                >
                  {t('nav.apply')}
                </button>
              </div>
            </div>
          </nav>
        </div>
      </>
    );
  }

  return (
    <nav className={`fixed top-5 left-1/2 -translate-x-1/2 z-50 transition-all duration-350 ${
      isScrolled
        ? 'bg-white/90 backdrop-blur-xl shadow-nav border border-[#E8EDF5]'
        : 'bg-white/70 backdrop-blur-md border border-[#E8EDF5]/50'
    } rounded-2xl px-4 py-2.5`}>
      <div className="flex items-center gap-1">
        {/* Logo */}
        <div className="flex items-center gap-2 mr-4 pr-4 border-r border-[#E8EDF5]">
          <div className="w-7 h-7 rounded-lg bg-[#0A1628] flex items-center justify-center">
            <Icon name="SparklesIcon" size={14} className="text-white" />
          </div>
          <span className="font-display font-bold text-[#0A1628] text-sm">Neuroakashic™</span>
        </div>

        {sections.map((section) => (
          <button
            key={section.anchor}
            onClick={() => scrollToSection(section.anchor)}
            className={`relative px-3 py-2 rounded-xl text-sm font-body font-medium transition-all duration-250 ${
              activeSection === section.anchor
                ? 'text-[#1D6FE8] bg-[#EEF4FD]'
                : 'text-[#4A5568] hover:text-[#0A1628] hover:bg-[#F4F7FC]'
            }`}
            title={section.tooltip}
          >
            {section.label}
          </button>
        ))}

        <div className="ml-2 pl-2 border-l border-[#E8EDF5] flex items-center gap-2">
          <LanguageSwitcher />
          <button
            onClick={scrollToApply}
            className="bg-[#0A1628] text-white font-display font-semibold text-sm px-4 py-2 rounded-xl hover:bg-[#1D6FE8] transition-all duration-250"
          >
            {t('nav.apply')}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default StickyAnchorNav;